import { z } from 'zod';

/**
 * Contracten van de module media: bestanden uploaden en downloaden.
 *
 * Het oude platform had een open uploadendpoint zonder aanmelding en de sleutels van de
 * objectopslag in de webbundel. Daarom staan hier twee dingen centraal: de client krijgt
 * nooit een sleutel te zien, en een download gaat altijd eerst langs een toegangscontrole.
 */

/**
 * Wat een bestand is en waar het bij hoort.
 *
 * `purpose` zegt waarvoor het bedoeld is; de eigenaar-module bepaalt welke waarden er
 * bestaan. `media` kent er de betekenis niet van en gebruikt hem alleen voor de limieten
 * en de opslagsleutel.
 */
export const mediaPurposeSchema = z.enum([
  'tenant_logo',
  // Het eigen icoon van een snelkoppeling (FBK-8). Iedereen van het bedrijf mag het zien.
  'shortcut_icon',
  'profile_photo',
  'post_attachment',
  'import_file',
  // Een versie van een werkinstructie (Stap 20). Apart van `post_attachment`, want de
  // toegangsvraag is een andere: niet "wie kreeg het bericht" maar "wie kreeg déze versie".
  'document_version',
  // Een foto bij een ingevuld formulier (Stap 37). Apart van `post_attachment`: de
  // toegangsvraag is "mag jij deze inzending lezen", en dat is de inzender of zijn behandelaar.
  'form_photo',
  // Een foto bij een melding vanaf de vloer (Stap 36). Apart doel: bij een anonieme melding mag
  // het bestand niet aan een persoon te koppelen zijn, en de toegangsvraag is "mag jij deze
  // melding lezen".
  'floor_report_photo',
  // Een foto bij een afgevinkt checklist-item (Stap 38): het bewijs dat de koelcel schoon was.
  'checklist_photo',
  // Een foto bij een ploegoverdracht (Stap 39): de lekkende pomp, de bijgevulde voorraad. De
  // toegangsvraag is "mag jij de overdrachten van deze lijn lezen".
  'handover_photo',
  // Een bestand in een persoonlijk gesprek (BER-2). Apart van `post_attachment`, want de
  // toegangsvraag is een heel andere: niet "wie kreeg het bericht" maar "wie zit in dit gesprek" —
  // en die vraag kent geen beheerder die meekijkt.
  'message_attachment',
  // Het merk van het product zelf (MRK-1): productlogo licht en donker, en het favicon. Alleen een
  // platformbeheerder uploadt ze, en ze horen bij geen enkel bedrijf (ADR-0084).
  'platform_logo',
  'favicon',
  // De achtergrond van het donkere vlak op het aanmeldscherm, van het platform of van een bedrijf.
  'login_background',
]);
export type MediaPurpose = z.infer<typeof mediaPurposeSchema>;

export const mediaKindSchema = z.enum(['image', 'pdf', 'video', 'data']);
export type MediaKind = z.infer<typeof mediaKindSchema>;

export const mediaStatusSchema = z.enum(['pending', 'ready', 'rejected']);
export type MediaStatus = z.infer<typeof mediaStatusSchema>;

export const fileMetadataSchema = z.object({
  id: z.guid(),
  ownerModule: z.string(),
  ownerRef: z.string().nullable(),
  purpose: mediaPurposeSchema,
  originalName: z.string(),
  mimeType: z.string(),
  sizeBytes: z.number().int(),
  status: mediaStatusSchema,
  width: z.number().int().nullable(),
  height: z.number().int().nullable(),
  hasThumbnail: z.boolean(),
  rejectedReason: z.string().nullable(),
  createdAt: z.string().datetime(),
});
export type FileMetadata = z.infer<typeof fileMetadataSchema>;

export const fileMetadataListSchema = z.object({ items: z.array(fileMetadataSchema) });
export type FileMetadataList = z.infer<typeof fileMetadataListSchema>;

// --- Uploaden ------------------------------------------------------------------------

/**
 * De aanvraag voor een upload. De client stuurt wat hij van het bestand weet; de server
 * bepaalt of dat mag en waar het terechtkomt.
 */
export const createUploadRequestSchema = z.object({
  ownerModule: z.string().min(2).max(32),
  purpose: mediaPurposeSchema,
  fileName: z.string().min(1).max(255),
  mimeType: z.string().min(3).max(128),
  sizeBytes: z.number().int().min(1),
  /**
   * Bij welk bedrijf dit bestand hoort. Alleen voor een platformbeheerder, die een
   * bedrijfslogo instelt en zelf geen bedrijf in zijn sessie heeft. Een medewerker laat hem
   * weg; stuurt hij hem toch mee, dan wordt hij genegeerd, anders was het een weg om over de
   * bedrijfsgrens te schrijven.
   *
   * Voor het platformmerk (`platform_logo`, `favicon`, `login_background`) laat de
   * platformbeheerder hem weg: zo'n bestand hoort bij geen bedrijf (ADR-0084).
   */
  tenantId: z.guid().optional(),
});
export type CreateUploadRequest = z.infer<typeof createUploadRequestSchema>;

/**
 * Het antwoord: een id en een URL waar de browser het bestand rechtstreeks naartoe PUT.
 * Tien minuten geldig — lang genoeg voor een video van vijftig megabyte op een trage
 * verbinding, kort genoeg om een gelekte URL waardeloos te maken.
 */
export const uploadTicketSchema = z.object({
  fileId: z.guid(),
  uploadUrl: z.string().url(),
  /** Headers die de PUT mee moet sturen; anders klopt de handtekening niet. */
  headers: z.record(z.string(), z.string()),
  expiresAt: z.string().datetime(),
});
export type UploadTicket = z.infer<typeof uploadTicketSchema>;

export const mediaLimitsSchema = z.object({
  image: z.number().int(),
  pdf: z.number().int(),
  video: z.number().int(),
  data: z.number().int(),
});
export type MediaLimits = z.infer<typeof mediaLimitsSchema>;

export const attachRequestSchema = z.object({
  fileId: z.guid().nullable(),
});
export type AttachRequest = z.infer<typeof attachRequestSchema>;

/**
 * Waar de browser het bestand ophaalt.
 *
 * Een URL in JSON en geen `302`. Dat lijkt een omweg, maar een doorverwijzing werkt hier
 * niet: het verzoek aan de API heeft een `Authorization`-header nodig, en een browser stuurt
 * die bij het volgen van de doorverwijzing mee naar de opslag. Die ziet dan twee manieren van
 * aanmelden — de header én de handtekening in de URL — en weigert het verzoek.
 *
 * Met de URL in het antwoord doet de browser een tweede, schoon verzoek: precies wat een
 * `<img src>` ook doet.
 */
export const downloadLinkSchema = z.object({
  url: z.string().url(),
  expiresAt: z.string().datetime(),
});
export type DownloadLink = z.infer<typeof downloadLinkSchema>;
